import type { AxiosInstance } from 'axios';
import { unwrap, unwrapItems, type ObjectResponse } from '../core/envelope';
import type { PrintFormatColumn } from '../data/printFormat';

/**
 * Report column layout — the designer's persistence.
 *
 * `replace` is the designer's Save: it posts the entire layout and the server
 * deletes any column absent from the payload. `upsert` exists for partial edits
 * that must not disturb the rest of the layout — reach for it only when you
 * genuinely mean "change these, leave everything else alone", because it can
 * never express a removal.
 */
export function createPrintFormatColumnService(client: AxiosInstance) {
  return {
    /** Full layout for a format, including columns currently hidden. */
    forFormat: async (printFormatId: string): Promise<PrintFormatColumn[]> => {
      const res = await client.post<ObjectResponse<PrintFormatColumn[]>>(
        '/PrintFormatColumn/for-format',
        {},
        { params: { printFormatId } },
      );
      return unwrapItems(res.data);
    },

    /**
     * Replaces the whole layout. Columns missing from `items` are deleted, so
     * always send the complete set the designer is showing.
     */
    replace: async (printFormatId: string, items: PrintFormatColumn[]): Promise<boolean> => {
      const res = await client.post<ObjectResponse<boolean>>(
        '/PrintFormatColumn/replace',
        { items },
        { params: { printFormatId } },
      );
      return unwrap(res.data) ?? false;
    },

    /** Insert-or-update individual columns, leaving the rest untouched. */
    upsert: async (items: PrintFormatColumn[]): Promise<boolean> => {
      const res = await client.post<ObjectResponse<boolean>>(
        '/PrintFormatColumn/upsert',
        { items },
      );
      return unwrap(res.data) ?? false;
    },

    remove: async (column: PrintFormatColumn): Promise<boolean> => {
      const res = await client.post<ObjectResponse<boolean>>(
        '/PrintFormatColumn/Delete',
        column,
      );
      return unwrap(res.data) ?? false;
    },
  };
}

export type PrintFormatColumnService = ReturnType<typeof createPrintFormatColumnService>;
